import { useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from '@tanstack/react-router';
import { Alert, Button, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconBuildingWarehouse, IconPlus } from '@tabler/icons-react';
import { PERMISSIONS_KEY, usePermissions } from '../auth/usePermissions';
import { useCurrentWarehouseStore } from './currentWarehouse';
import { useReachableWarehouses } from './useReachableWarehouses';
import { useWarehouses } from './useWarehouses';

interface Props {
  children: ReactNode;
}

/** Creating warehouses. The one thing somebody can usefully do before any exist. */
const WAREHOUSE_WRITE = 'warehouse:write';

/**
 * Nothing inside the app runs until we know which warehouse it is running in.
 *
 * Permissions resolve per warehouse, so before one is chosen the navigation menu cannot be built
 * and every warehouse-scoped endpoint answers 403. Four states:
 *
 *   NO WAREHOUSES EXIST  a brand-new tenant. Nothing is wrong with the account — somebody has to
 *                        create the first warehouse, and if this user can, they are sent to do it.
 *   NONE REACHABLE       warehouses exist and this account has not been given any of them.
 *   NOT YET CHOSEN       pick the first one.
 *   CHOSEN               render the app.
 *
 * **Why "no warehouses exist" is separate.** Both look identical from `/auth/me/warehouses` — an
 * empty list either way — and conflating them told the founder of a brand-new tenant to contact
 * their administrator. They ARE the administrator, and the app had no way to let them fix it.
 * Nothing seeds a warehouse at provisioning, so this is the first screen every new customer sees.
 *
 * **Why no "choose your warehouse" step.** Legacy shows one at sign-in whenever somebody has more
 * than one domain, and it is a screen most people click past every single day. Landing where you
 * were last and switching from the header is fewer steps for the common case and exactly as many
 * for the rare one.
 *
 * **Why a stored warehouse is re-checked.** Access can be revoked between sessions. A stored id
 * that is no longer reachable would send a header the server refuses on every request, and the
 * app would look broken rather than say what happened.
 */
export function WarehouseGate({ children }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const permissions = usePermissions();
  const reachable = useReachableWarehouses();
  const tenantWarehouses = useWarehouses();
  const warehouseId = useCurrentWarehouseStore((s) => s.warehouseId);
  const setWarehouse = useCurrentWarehouseStore((s) => s.setWarehouse);

  const rows = reachable.data;

  useEffect(() => {
    if (!rows) return;

    if (rows.length === 0) {
      // Cleared rather than left behind: if access was revoked, continuing to send it means every
      // request is refused and the reason is buried in a 403 nobody reads.
      if (warehouseId) setWarehouse(null);
      return;
    }

    if (warehouseId && rows.some((w) => w.warehouseId === warehouseId)) return;

    setWarehouse(rows[0]);
    // The permission list is per warehouse, so a warehouse arriving or changing makes the cached
    // answer wrong. Dropped rather than refetched: refetching leaves the old menu on screen for a
    // moment, and the old menu is the one built for a warehouse the user is no longer in.
    void queryClient.invalidateQueries({ queryKey: PERMISSIONS_KEY });
  }, [rows, warehouseId, setWarehouse, queryClient]);

  if (reachable.isPending) {
    return <Waiting />;
  }

  // A failed lookup is NOT "you have no warehouses". Telling somebody their account is
  // unconfigured because a request timed out would send them to their administrator for nothing.
  if (reachable.isError) {
    return (
      <Center h="60vh" px="md">
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertCircle size={18} />}
          title={t('warehouseGate.lookupFailedTitle')}
          maw={520}
        >
          {t('warehouseGate.lookupFailed')}
        </Alert>
      </Center>
    );
  }

  if (rows && rows.length === 0) {
    // Still loading the tenant's own list — wait rather than guess which empty state this is.
    if (tenantWarehouses.isPending) {
      return <Waiting />;
    }

    const tenantHasNone = tenantWarehouses.isSuccess && (tenantWarehouses.data?.length ?? 0) === 0;

    if (tenantHasNone && permissions.has(WAREHOUSE_WRITE)) {
      // The warehouses screen itself is let through, because it is where the fix is. Checked by
      // path rather than by rendering everything: every other screen would fire warehouse-scoped
      // requests that can only be refused, and a dashboard of 403s is not an invitation to do
      // anything.
      if (pathname.startsWith('/app/warehouses')) {
        return <>{children}</>;
      }
      return <NoWarehousesYet />;
    }

    return <NotSetUpYet />;
  }

  // One render can fall between the list arriving and the effect above storing a warehouse.
  // Waiting avoids every screen mounting with no warehouse and firing a wave of refused requests.
  if (!warehouseId) {
    return <Waiting />;
  }

  return <>{children}</>;
}

function Waiting() {
  return (
    <Center h="60vh">
      <Loader color="invetrak" />
    </Center>
  );
}

/** A brand-new tenant, and somebody who can do something about it. */
function NoWarehousesYet() {
  const { t } = useTranslation();

  return (
    <Center h="60vh" px="md">
      <Stack align="center" gap="md" maw={520}>
        <IconBuildingWarehouse size={48} opacity={0.4} />
        <Title order={3} ta="center">
          {t('warehouseGate.noneExistTitle')}
        </Title>
        <Text ta="center" c="dimmed">
          {t('warehouseGate.noneExist')}
        </Text>
        <Button
          component={Link}
          to="/app/warehouses"
          color="invetrak"
          leftSection={<IconPlus size={16} />}
        >
          {t('warehouseGate.createFirst')}
        </Button>
      </Stack>
    </Center>
  );
}

/**
 * Signed in, and nothing to do until somebody grants them a warehouse.
 *
 * Legacy answers this case with "Invalid user/password provided" — a correct password reported as
 * wrong, sending somebody off to reset a password that was never the problem. This says what is
 * actually missing and who fixes it.
 *
 * The account menu stays reachable above this, so somebody stuck here can still change their
 * language, save recovery codes and sign out.
 */
function NotSetUpYet() {
  const { t } = useTranslation();

  return (
    <Center h="60vh" px="md">
      <Stack align="center" gap="md" maw={520}>
        <IconBuildingWarehouse size={48} opacity={0.4} />
        <Title order={3} ta="center">
          {t('warehouseGate.notSetUpTitle')}
        </Title>
        <Text ta="center" c="dimmed">
          {t('warehouseGate.notSetUp')}
        </Text>
      </Stack>
    </Center>
  );
}
